import { Input, SimpleGrid, Text } from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import { buildContestDateBounds } from './dateHelpers'
import { IContestFormTabProps } from './types'

interface IDateFieldProps {
  id: string
  label: string
  value: string
  /** Omitted when a stale stored date would otherwise make the range impossible (min > max). */
  min?: string
  /** Omitted on the last date, which has no upper bound. */
  max?: string
  error?: string
  /** Soft ordering hint, only rendered when there is no hard error. */
  warning?: string
  helper?: string
  disabled: boolean
  onChange: (value: string) => void
}

function DateField({ id, label, value, min, max, error, warning, helper, disabled, onChange }: IDateFieldProps) {
  return (
    // O aviso ocupa o lugar do `helperText`: ele já só aparecia quando não havia erro, que é
    // exatamente a regra do `FormField`, e assim as três mensagens nunca se empilham.
    <FormField
      label={label}
      htmlFor={id}
      isRequired
      error={error}
      helperText={
        warning ? (
          <Text as="span" color="orange.500">
            ⚠ {warning}
          </Text>
        ) : (
          helper
        )
      }
    >
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        type="date"
        id={id}
        disabled={disabled}
        maxLength={255}
      />
    </FormField>
  )
}

interface IProps extends IContestFormTabProps {
  onClearError: (field: string) => void
  /** True when editing or duplicating an existing contest, whose dates can already be in the past. */
  allowLegacyDates: boolean
}

export function DatesTab({ form, setForm, errors, saving, onClearError, allowLegacyDates }: IProps) {
  const { bounds, rd, sd, dd, od, wds, wde, warnings } = buildContestDateBounds(form, allowLegacyDates)

  const handleChange = (field: keyof typeof form) => (value: string) => {
    setForm({ ...form, [field]: value })
    onClearError(field as string)
  }

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={3}>
      <DateField
        id="registration_deadline"
        label="Data final para inscrição"
        value={rd}
        min={bounds.registration_deadline.min}
        max={bounds.registration_deadline.max}
        error={errors.registration_deadline}
        warning={warnings.registration_deadline}
        disabled={saving}
        onChange={handleChange('registration_deadline')}
      />

      <DateField
        id="submission_deadline"
        label="Data final para envio dos dados das obras"
        value={sd}
        min={bounds.submission_deadline.min}
        max={bounds.submission_deadline.max}
        error={errors.submission_deadline}
        warning={warnings.submission_deadline}
        disabled={saving}
        onChange={handleChange('submission_deadline')}
      />

      <DateField
        id="delivery_deadline"
        label="Data final de entrega das obras"
        value={dd}
        min={bounds.delivery_deadline.min}
        max={bounds.delivery_deadline.max}
        error={errors.delivery_deadline}
        warning={warnings.delivery_deadline}
        disabled={saving}
        onChange={handleChange('delivery_deadline')}
      />

      <DateField
        id="opening_event_date"
        label="Data do Evento de Abertura (Mostra)"
        value={od}
        min={bounds.opening_event_date.min}
        max={bounds.opening_event_date.max}
        error={errors.opening_event_date}
        warning={warnings.opening_event_date}
        disabled={saving}
        onChange={handleChange('opening_event_date')}
      />

      <DateField
        id="withdraw_start_date"
        label="Data inicial de retirada das obras"
        value={wds}
        min={bounds.withdraw_start_date.min}
        max={bounds.withdraw_start_date.max}
        error={errors.withdraw_start_date}
        warning={warnings.withdraw_start_date}
        disabled={saving}
        onChange={handleChange('withdraw_start_date')}
      />

      <DateField
        id="withdraw_end_date"
        label="Data final de retirada das obras"
        value={wde}
        min={bounds.withdraw_end_date.min}
        error={errors.withdraw_end_date}
        warning={warnings.withdraw_end_date}
        helper="Último dia sem cobrança de taxa de armazenamento."
        disabled={saving}
        onChange={handleChange('withdraw_end_date')}
      />
    </SimpleGrid>
  )
}
